import { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { NavLink } from "react-router-dom";
import { addDisabledStateToMenu } from "../utils/permissions";
import "./Homepage.css";

// WMO weather code map
const WMO = {
  0:  { icon: 'ti ti-sun',           label: 'Clear Sky' },
  1:  { icon: 'ti ti-sun',           label: 'Mainly Clear' },
  2:  { icon: 'ti ti-cloud-sun',     label: 'Partly Cloudy' },
  3:  { icon: 'ti ti-cloud',         label: 'Overcast' },
  45: { icon: 'ti ti-cloud-fog',     label: 'Foggy' },
  48: { icon: 'ti ti-cloud-fog',     label: 'Rime Fog' },
  51: { icon: 'ti ti-cloud-drizzle', label: 'Light Drizzle' },
  53: { icon: 'ti ti-cloud-drizzle', label: 'Drizzle' },
  55: { icon: 'ti ti-cloud-drizzle', label: 'Heavy Drizzle' },
  61: { icon: 'ti ti-cloud-rain',    label: 'Light Rain' },
  63: { icon: 'ti ti-cloud-rain',    label: 'Rain' },
  65: { icon: 'ti ti-cloud-rain',    label: 'Heavy Rain' },
  71: { icon: 'ti ti-snowflake',     label: 'Light Snow' },
  73: { icon: 'ti ti-snowflake',     label: 'Snow' },
  75: { icon: 'ti ti-snowflake',     label: 'Heavy Snow' },
  80: { icon: 'ti ti-cloud-storm',   label: 'Showers' },
  82: { icon: 'ti ti-cloud-storm',   label: 'Heavy Showers' },
  95: { icon: 'ti ti-storm',         label: 'Thunderstorm' },
};
const getWmo = (code) => WMO[code] ?? { icon: 'ti ti-cloud', label: 'Unknown' };

// Darken a hex color
function darken(hex, amt) {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (n >> 16) + amt));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amt));
  const b = Math.min(255, Math.max(0, (n & 0xff) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

const CARDS_BASE = (counts) => [
  {
    title: "Appointments",
    url: "/Admin/appointment2",
    icon: "ti ti-calendar-event",
    color: "#FF9500",
    wide: true,
  },
  {
    title: "Patients",
    count: counts.totalPatients,
    countLabel: "Total",
    url: "/Admin/patient",
    icon: "ti ti-users",
    color: "#34C759",
    wide: true,
  },
  {
    title: "Medicals",
    url: "/Admin/Medicals",
    icon: "ti ti-heart-rate-monitor",
    color: "#FF2D55",
  },
  {
    title: "Pharmacy",
    url: "/Admin/Pharmacy",
    icon: "ti ti-pill",
    color: "#FF3B30",
  },
  {
    title: "Dashboard",
    url: "/Admin/dashboard",
    icon: "ti ti-layout-dashboard",
    color: "#5856D6",
  },
  {
    title: "Laboratory",
    url: "/Admin/ManageLabs",
    icon: "ti ti-flask",
    color: "#32ADE6",
  },
  {
    title: "Insurance",
    url: "/Admin/Insurance",
    icon: "ti ti-shield-check",
    color: "#0A84FF",
  },
  {
    title: "Billing",
    url: "/Admin/Billing",
    icon: "ti ti-receipt",
    color: "#30D158",
  },
  {
    title: "Reports",
    url: "/Admin/ReportsInc",
    icon: "ti ti-chart-bar",
    color: "#BF5AF2",
  },
  {
    title: "Settings",
    url: "/Admin/setting",
    icon: "ti ti-settings-2",
    color: "#636366",
  },
];

function AppCard({ card }) {
  const isDisabled = card.disabled;

  const inner = (
    <div
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "22px 12px 16px",
        boxShadow: "0 2px 14px rgba(26,43,60,0.09)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        height: "100%",
        opacity: isDisabled ? 0.45 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.transform = "translateY(-3px)";
          e.currentTarget.style.boxShadow = `0 8px 24px ${card.color}33`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 14px rgba(26,43,60,0.09)";
      }}
    >
      {/* Count badge */}
      {card.count != null && (
        <span
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            background: card.color,
            color: "#fff",
            borderRadius: 20,
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            lineHeight: 1.5,
          }}
        >
          {card.count} {card.countLabel}
        </span>
      )}

      {/* iOS icon badge */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: `linear-gradient(145deg, ${card.color}, ${darken(card.color, -30)})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 6px 16px ${card.color}55`,
          flexShrink: 0,
        }}
      >
        <i className={card.icon} style={{ color: "#fff", fontSize: 30 }} />
      </div>

      {/* Label */}
      <span
        style={{
          fontWeight: 600,
          fontSize: 13,
          color: "#3a4a5c",
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {card.title}
      </span>
    </div>
  );

  if (!card.url || isDisabled) return <div style={{ height: "100%" }}>{inner}</div>;

  if (card.external) {
    return (
      <a
        href={card.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", display: "block", height: "100%" }}
      >
        {inner}
      </a>
    );
  }

  return (
    <NavLink
      to={card.url}
      style={{ textDecoration: "none", display: "block", height: "100%" }}
    >
      {inner}
    </NavLink>
  );
}

const Homepage = () => {
  const [appointments, setAppointments] = useState([]);
  const [counts, setCounts] = useState({});
  const [cardsWithPermissions, setCardsWithPermissions] = useState([]);
  const [weather, setWeather] = useState(null);
  const [now, setNow] = useState(new Date());
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [hrSystemEnabled, setHrSystemEnabled] = useState(false);
  const [pineHrLink, setPineHrLink] = useState('');

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Weather — Open-Meteo, free, no API key
  useEffect(() => {
    fetch('https://api.open-meteo.com/v1/forecast?latitude=26.2235&longitude=50.5876&current=temperature_2m,weather_code,apparent_temperature,relative_humidity_2m&timezone=Asia%2FBahrain')
      .then(r => r.json())
      .then(d => {
        const c = d.current;
        setWeather({
          temp: Math.round(c.temperature_2m),
          feelsLike: Math.round(c.apparent_temperature),
          humidity: c.relative_humidity_2m,
          wmo: getWmo(c.weather_code),
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get("Homepage/total_counts")
      .then((res) => { if (res.data.success) setCounts(res.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get("getModuleSettings")
      .then((res) => {
        if (res.data?.success) {
          const loyaltyRow = res.data.data.find(m => m.module_name === 'loyalty_system');
          setLoyaltyEnabled(Boolean(loyaltyRow?.enable));
          const hrRow = res.data.data.find(m => m.module_name === 'hr_system');
          setHrSystemEnabled(Boolean(hrRow?.enable));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get("getPineHrSetting")
      .then((res) => {
        if (res.data?.success) setPineHrLink(res.data.data?.link || '');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get("getUpcomingAppointment")
      .then((res) => { if (res.data.success) setAppointments(res.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const cards = CARDS_BASE(counts);
    if (hrSystemEnabled && pineHrLink) {
      cards.push({
        title: 'HR System',
        url: pineHrLink,
        icon: 'ti ti-fingerprint',
        color: '#7C5CBF',
        external: true,
      });
    }
    if (loyaltyEnabled) {
      cards.push({
        title: 'Loyalty System',
        url: '/Admin/LoyaltySystem',
        icon: 'ti ti-award',
        color: '#F29E4C',
      });
    }
    cards.push({
      title: 'Kiosk',
      url: '/Admin/Kiosk',
      icon: 'ti ti-device-tablet',
      color: '#6C63FF',
    });
    addDisabledStateToMenu(cards).then(setCardsWithPermissions);
  }, [counts, loyaltyEnabled, hrSystemEnabled, pineHrLink]);

  const fmt = (timeStr) =>
    new Date(`1970-01-01T${timeStr}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  return (
    <section className="homepage-section" style={{ position: "relative", width: "100%", height: "100vh", overflow: "hidden" }}>

      {/* Background video */}
      <video
        className="homepage-bg-video"
        autoPlay muted loop playsInline
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}
      >
        <source src="/homeVideo.mp4" type="video/mp4" />
      </video>

      {/* Light overlay so cards stay legible */}
      <div className="homepage-bg-overlay" style={{ position: "absolute", inset: 0, background: "rgba(232, 238, 245, 0.55)", backdropFilter: "blur(2px)", zIndex: 1 }} />

      {/* Content */}
      <div
        className="homepage-content"
        style={{
          position: "relative",
          zIndex: 2,
          height: "100vh",
          boxSizing: "border-box",
          padding: "10vh 24px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
      {/* Weather + Clock Header */}
      <div className="homepage-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>

        {/* Left: Weather */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 15,
            background: 'linear-gradient(145deg, #FF9500, #cc7800)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px #FF950055', flexShrink: 0,
          }}>
            <i className={weather?.wmo.icon ?? 'ti ti-cloud'} style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#1a2b3c', lineHeight: 1 }}>
                {weather ? `${weather.temp}°C` : '--'}
              </span>
              <span style={{ fontSize: 14, color: '#6b7a8d' }}>
                {weather?.wmo.label ?? 'Loading...'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#9aa0ac', marginTop: 3 }}>
              {weather ? `Feels like ${weather.feelsLike}°C · ${weather.humidity}% RH` : ''}
            </div>
            <div style={{ fontSize: 12, color: '#9aa0ac' }}>Manama, Bahrain</div>
          </div>
        </div>

        {/* Right: Live clock */}
        <div className="homepage-clock-block" style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a2b3c', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
          </div>
          <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 4 }}>
            {now.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>

      </div>

      <div className="row g-0" style={{ gap: 0 }}>
        {/* Left: cards grid */}
        <div className="col-lg-8 col-12 homepage-cards-col" style={{ paddingRight: 16 }}>
          <div className="row g-3">
            {cardsWithPermissions.map((card, i) => (
              <div
                className={card.wide ? "col-12 col-sm-6 col-md-6" : "col-6 col-sm-4 col-md-3"}
                key={i}
              >
                <AppCard card={card} />
              </div>
            ))}
          </div>
        </div>

        {/* Right: Today's Appointments */}
        <div className="col-lg-4 col-12" style={{ paddingTop: 0 }}>
          <div
            className="homepage-appt-card"
            style={{
              background: "#fff",
              borderRadius: 20,
              boxShadow: "0 2px 14px rgba(26,43,60,0.09)",
              padding: "20px",
              height: "calc(100vh - 280px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Panel header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "linear-gradient(145deg, #FF9500, #cc7800)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px #FF950055",
                  flexShrink: 0,
                }}
              >
                <i className="ti ti-clock" style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div>
                <h6 style={{ margin: 0, fontWeight: 700, color: "#1a2b3c" }}>
                  Today's Appointments
                </h6>
                <span style={{ fontSize: 11, color: "#9aa0ac" }}>
                  {appointments.length} scheduled
                </span>
              </div>
            </div>

            {/* Scrollable list */}
            <div style={{ overflowY: "auto", flexGrow: 1 }}>
              {appointments.length > 0 ? (
                appointments.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      background: i % 2 === 0 ? "#f7f9fc" : "#fff",
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#1a2b3c",
                        marginBottom: 2,
                      }}
                    >
                      {item.displayName}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#6b7a8d" }}>
                        {item.patientName}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#FF9500",
                          fontWeight: 600,
                          background: "#FF950015",
                          borderRadius: 6,
                          padding: "2px 7px",
                        }}
                      >
                        {fmt(item.startTime)} – {fmt(item.endTime)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "#9aa0ac",
                    paddingTop: 40,
                  }}
                >
                  <i
                    className="ti ti-calendar-off"
                    style={{ fontSize: 36, opacity: 0.35, marginBottom: 8 }}
                  />
                  <span style={{ fontSize: 13 }}>No appointments today</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

export default Homepage;
